import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, Statistic, Card, Row, Col, DatePicker, Tooltip, Progress, message, Button } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, BarChartOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import supabaseRepository from '../supabaseRepository';
import repository from '../repository';
import { OrderProgressDto } from '../apiTypes';
import './scheduleLogsModal.css';

const { RangePicker } = DatePicker;

interface ScheduleLogsModalProps {
    visible: boolean;
    onClose: () => void;
}

interface DailyScheduleStats {
    dailyStats: Record<string, {
        date: string;
        totalLogs: number;
        successLogs: number;
        failLogs: number;
        orderTaskCounts: Record<string, { total: number; success: number; fail: number }>;
        errorReasons: Record<string, number>;
    }>;
    orderTaskTotals: Record<string, { total: number; success: number; fail: number }>;
}

const ScheduleLogsModal: React.FC<ScheduleLogsModalProps> = ({
    visible,
    onClose
}) => {
    const [loading, setLoading] = useState(false);
    const [dailyData, setDailyData] = useState<DailyScheduleStats | null>(null);
    const [last24HourData, setLast24HourData] = useState<DailyScheduleStats | null>(null);
    const [orderTasksInfo, setOrderTasksInfo] = useState<Record<string, OrderProgressDto>>({});
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
        dayjs().subtract(7, 'days'),
        dayjs()
    ]);
    const [dailyTablePageSize, setDailyTablePageSize] = useState(10);
    const [orderTaskTablePageSize, setOrderTaskTablePageSize] = useState(15);
    const [taskTypeFilter, setTaskTypeFilter] = useState<'all' | 'follow' | 'like'>('all');

    const fetchDailyScheduleData = async () => {
        setLoading(true);
        try {
            let startDate, endDate;
            if (dateRange) {
                startDate = dayjs(dateRange[0]).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
                endDate = dayjs(dateRange[1]).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');
            }

            // 일자별 통계 데이터 가져오기 (dateRange 기반)
            const data = await supabaseRepository.getDailyScheduleLogsByOrderTasks(startDate, endDate);
            setDailyData(data);

            // 최근 24시간 OrderTask별 통계 데이터 가져오기
            const last24HourStart = dayjs().subtract(24, 'hours').utc().format('YYYY-MM-DD HH:mm:ss');
            const last24HourEnd = dayjs().utc().format('YYYY-MM-DD HH:mm:ss');
            const last24HourStats = await supabaseRepository.getDailyScheduleLogsByOrderTasks(last24HourStart, last24HourEnd);
            setLast24HourData(last24HourStats);

            // OrderTask 정보 가져오기 (최근 24시간 기준)
            const orderTaskIds = Object.keys(last24HourStats.orderTaskTotals);
            const orderTasksInfoMap: Record<string, OrderProgressDto> = {};

            await Promise.all(
                orderTaskIds.map(async (orderTaskId) => {
                    try {
                        const orderTaskInfo = await repository.getOrderTask(orderTaskId);
                        orderTasksInfoMap[orderTaskId] = orderTaskInfo;
                    } catch (error) {
                        console.error(`Error fetching orderTask ${orderTaskId}:`, error);
                    }
                })
            );

            setOrderTasksInfo(orderTasksInfoMap);
        } catch (error) {
            console.error('Error fetching daily schedule data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            fetchDailyScheduleData();
        }
    }, [visible, dateRange]);

    const handleDateRangeChange = (dates: any) => {
        setDateRange(dates);
    };

    // 일자별 데이터를 테이블 형태로 변환
    const getDailyTableData = () => {
        if (!dailyData) return [];

        return Object.values(dailyData.dailyStats)
            .sort((a, b) => moment(b.date).unix() - moment(a.date).unix())
            .map(dayData => ({
                key: dayData.date,
                date: dayData.date,
                totalLogs: dayData.totalLogs,
                successLogs: dayData.successLogs,
                failLogs: dayData.failLogs,
                successRate: dayData.totalLogs > 0 ? ((dayData.successLogs / dayData.totalLogs) * 100).toFixed(1) : '0',
                orderTaskCounts: dayData.orderTaskCounts,
                errorReasons: dayData.errorReasons
            }));
    };

    // orderTask별 총 실행 횟수 테이블 데이터 (최근 24시간 기준)
    const getOrderTaskTableData = () => {
        if (!last24HourData) return [];

        return Object.entries(last24HourData.orderTaskTotals)
            .map(([orderTaskId, data]) => {
                const orderTaskInfo = orderTasksInfo[orderTaskId];
                const taskType = orderTaskInfo?.taskType || 'unknown';
                const errorRate = data.total > 0 ? ((data.fail / data.total) * 100) : 0;
                return {
                    key: orderTaskId,
                    orderTaskId,
                    taskType: taskType,
                    targetId: orderTaskInfo?.targetId || 'N/A',
                    totalCnt: orderTaskInfo?.totalCnt || 0,
                    progressInfo: {
                        progressCnt: orderTaskInfo?.progressCnt || 0,
                        totalCnt: orderTaskInfo?.totalCnt || 0
                    },
                    orderDate: orderTaskInfo?.orderDate || '',
                    orderNo: orderTaskInfo?.orderNo || null,
                    success: data.success,
                    fail: data.fail,
                    total: data.total,
                    successRate: data.total > 0 ? ((data.success / data.total) * 100).toFixed(1) : '0',
                    errorRate: errorRate
                };
            })
            .filter(item => {
                // taskTypeFilter에 따라 필터링
                if (taskTypeFilter === 'all') return true;
                if (taskTypeFilter === 'like') {
                    // like, like_reels 둘 다 포함
                    return item.taskType === 'like' || item.taskType === 'like_reels';
                }
                return item.taskType === taskTypeFilter;
            })
            .sort((a, b) => b.errorRate - a.errorRate); // 에러율이 높은 순서로 정렬
    };

    const dailyColumns = [
        {
            title: '날짜',
            dataIndex: 'date',
            key: 'date',
            render: (date: string) => moment(date).format('MM/DD (ddd)'),
            sorter: (a: any, b: any) => moment(a.date).unix() - moment(b.date).unix(),
        },
        {
            title: '총 실행',
            dataIndex: 'totalLogs',
            key: 'totalLogs',
            sorter: (a: any, b: any) => a.totalLogs - b.totalLogs,
            render: (value: number) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{value}</span>
        },
        {
            title: '성공',
            dataIndex: 'successLogs',
            key: 'successLogs',
            sorter: (a: any, b: any) => a.successLogs - b.successLogs,
            render: (value: number) => <span style={{ color: '#52c41a' }}>{value}</span>
        },
        {
            title: '실패',
            dataIndex: 'failLogs',
            key: 'failLogs',
            sorter: (a: any, b: any) => a.failLogs - b.failLogs,
            render: (value: number) => <span style={{ color: '#ff4d4f' }}>{value}</span>
        },
        {
            title: '에러 원인',
            dataIndex: 'errorReasons',
            key: 'errorReasons',
            width: 250,
            render: (errorReasons: Record<string, number>) => {
                if (!errorReasons || Object.keys(errorReasons).length === 0) {
                    return <span style={{ color: '#d9d9d9' }}>-</span>;
                }

                // 에러 수량이 높은 순으로 정렬
                const sortedReasons = Object.entries(errorReasons)
                    .sort(([, countA], [, countB]) => countB - countA);

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {sortedReasons.map(([reason, count]) => {
                            // reason에 따라 색상 결정
                            const color = reason === 'UNKNOWN' ? '#d9d9d9' :
                                        reason.includes('BLOCKED') ? '#ff4d4f' :
                                        reason.includes('ALREADY') ? '#faad14' :
                                        reason.includes('FAILED') ? '#ff7875' :
                                        '#f759ab';

                            return (
                                <Tag key={reason} color={color} style={{ margin: 0 }}>
                                    {reason} ({count})
                                </Tag>
                            );
                        })}
                    </div>
                );
            }
        },
        {
            title: '성공률',
            dataIndex: 'successRate',
            key: 'successRate',
            sorter: (a: any, b: any) => parseFloat(a.successRate) - parseFloat(b.successRate),
            render: (rate: string) => (
                <div>
                    <Progress
                        percent={parseFloat(rate)}
                        size="small"
                        strokeColor={
                            parseFloat(rate) >= 80 ? '#52c41a' :
                                parseFloat(rate) >= 60 ? '#faad14' : '#ff4d4f'
                        }
                    />
                    <span style={{ marginLeft: 8, fontSize: '12px' }}>{rate}%</span>
                </div>
            )
        },
        {
            title: 'OrderTask별 실행',
            dataIndex: 'orderTaskCounts',
            key: 'orderTaskCounts',
            render: (orderTaskCounts: Record<string, { total: number; success: number; fail: number }>) => (
                <Tooltip
                    title={
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {Object.entries(orderTaskCounts)
                                .sort(([, a], [, b]) => b.total - a.total)
                                .map(([taskId, data]) => (
                                    <div key={taskId} style={{ marginBottom: '4px', fontSize: '12px' }}>
                                        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{taskId}</div>
                                        <div>총: {data.total} | 성공: {data.success} | 실패: {data.fail}</div>
                                    </div>
                                ))
                            }
                        </div>
                    }
                    overlayStyle={{ maxWidth: '400px' }}
                >
                    <Tag color="blue">{Object.keys(orderTaskCounts).length}개 Task</Tag>
                </Tooltip>
            )
        }
    ];

    const orderTaskColumns = [
        {
            title: '작업 유형',
            dataIndex: 'taskType',
            key: 'taskType',
            width: 80,
            render: (taskType: string) => {
                const color = taskType === 'like' ? 'red' : taskType === 'follow' ? 'blue' : 'green';
                return <Tag color={color}>{taskType}</Tag>;
            }
        },
        {
            title: '대상 ID',
            dataIndex: 'targetId',
            key: 'targetId',
            width: 120,
            render: (targetId: string) => (
                <Button
                    type="text"
                    size="small"
                    style={{
                        padding: '2px 6px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        height: 'auto'
                    }}
                    onClick={() => {
                        navigator.clipboard.writeText(targetId);
                        message.success(`${targetId} 복사됨`);
                    }}
                    icon={<CopyOutlined />}
                >
                    {targetId}
                </Button>
            )
        },
        {
            title: '주문 수량',
            dataIndex: 'totalCnt',
            key: 'totalCnt',
            width: 100,
            sorter: (a: any, b: any) => a.totalCnt - b.totalCnt,
            render: (value: number) => <span style={{ fontWeight: 'bold' }}>{value?.toLocaleString()}회</span>
        },
        {
            title: '진행 현황',
            dataIndex: 'progressInfo',
            key: 'progressInfo',
            width: 120,
            render: (progressInfo: any) => {
                const percentage = progressInfo.totalCnt > 0 ? Math.floor(progressInfo.progressCnt / progressInfo.totalCnt * 100) : 0;
                return (
                    <div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                            {progressInfo.progressCnt} / {progressInfo.totalCnt}
                        </div>
                        <Progress
                            percent={percentage}
                            size="small"
                            strokeColor="#1890ff"
                            format={() => `${percentage}%`}
                        />
                    </div>
                );
            }
        },
        {
            title: '주문일자',
            dataIndex: 'orderDate',
            key: 'orderDate',
            width: 100,
            sorter: (a: any, b: any) => moment(a.orderDate).unix() - moment(b.orderDate).unix(),
            render: (date: string) => moment(date).format('MM/DD')
        },
        {
            title: '에러율',
            dataIndex: 'errorRate',
            key: 'errorRate',
            width: 100,
            sorter: (a: any, b: any) => a.errorRate - b.errorRate,
            render: (errorRate: number) => {
                const color = errorRate === 0 ? '#52c41a' :
                              errorRate < 10 ? '#faad14' :
                              errorRate < 30 ? '#ff7875' : '#ff4d4f';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Progress
                            percent={Math.min(100, errorRate)}
                            size="small"
                            strokeColor={color}
                            format={() => `${errorRate.toFixed(1)}%`}
                            style={{ width: '80px' }}
                        />
                    </div>
                );
            }
        },
        {
            title: '실행 현황',
            dataIndex: 'scheduleStatus',
            key: 'scheduleStatus',
            width: 150,
            sorter: (a: any, b: any) => (a.success + a.fail) - (b.success + b.fail),
            render: (_: any, record: any) => {
                const total = record.success + record.fail;
                const successRate = total > 0 ? Math.floor((record.success / total) * 100) : 0;
                const isGood = successRate >= 80;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            총 {total}회 실행
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Tag color={isGood ? '#52c41a' : '#ff4d4f'} style={{ margin: 0 }}>
                                성공 {record.success} | 실패 {record.fail}
                            </Tag>
                            {total > 0 && (
                                <span style={{
                                    fontSize: '11px',
                                    color: isGood ? '#52c41a' : '#ff4d4f',
                                    fontWeight: 'bold'
                                }}>
                                    {successRate}%
                                </span>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            title: '주문 링크',
            dataIndex: 'orderNo',
            key: 'orderNo',
            width: 100,
            render: (orderNo: number) => orderNo ? (
                <a
                    href={`http://gdadmin.amongkr.godomall.com/order/order_view.php?orderNo=${orderNo}`}
                    target='_blank'
                    rel="noreferrer noopener"
                >
                    <Button
                        type="primary"
                        size="small"
                        icon={<LinkOutlined />}
                    >
                        고도몰
                    </Button>
                </a>
            ) : (
                <span style={{ color: '#ccc' }}>N/A</span>
            )
        }
    ];

    // 총 통계 계산
    const getTotalStats = () => {
        if (!dailyData) return { totalLogs: 0, totalSuccess: 0, totalFail: 0, totalOrderTasks: 0 };

        const totalLogs = Object.values(dailyData.dailyStats).reduce((sum, day) => sum + day.totalLogs, 0);
        const totalSuccess = Object.values(dailyData.dailyStats).reduce((sum, day) => sum + day.successLogs, 0);
        const totalFail = Object.values(dailyData.dailyStats).reduce((sum, day) => sum + day.failLogs, 0);
        const totalOrderTasks = Object.keys(dailyData.orderTaskTotals).length;

        return { totalLogs, totalSuccess, totalFail, totalOrderTasks };
    };

    const totalStats = getTotalStats();

    return (
        <Modal
            title={
                <div>
                    <BarChartOutlined style={{ marginRight: 8 }} />
                    스케줄 작업 진행 현황
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                        일자별 OrderTask 실행 통계
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width="95%"
            style={{ maxWidth: '1600px', top: '20px' }}
            footer={null}
            className="schedule-logs-modal"
        >
            {/* 전체 통계 카드 */}
            {totalStats.totalLogs > 0 ? (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={6}>
                        <Statistic
                            title="총 실행 횟수"
                            value={totalStats.totalLogs}
                            valueStyle={{ color: '#1890ff', fontSize: window.innerWidth < 768 ? '18px' : '24px' }}
                            prefix={<BarChartOutlined />}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <Statistic
                            title="성공"
                            value={totalStats.totalSuccess}
                            valueStyle={{ color: '#52c41a', fontSize: window.innerWidth < 768 ? '18px' : '24px' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Col>
                    <Col xs={12} sm={6}>
                        <Statistic
                            title="실패"
                            value={totalStats.totalFail}
                            valueStyle={{ color: '#ff4d4f', fontSize: window.innerWidth < 768 ? '18px' : '24px' }}
                            prefix={<CloseCircleOutlined />}
                        />
                    </Col>
                </Row>
            ) : (
                <div style={{
                    padding: '24px',
                    textAlign: 'center',
                    background: '#fafafa',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px dashed #d9d9d9'
                }}>
                    <div style={{ fontSize: '16px', color: '#8c8c8c', marginBottom: '8px' }}>
                        � 선택한 기간에 스케줄 실행 기록이 없습니다
                    </div>
                    <div style={{ fontSize: '14px', color: '#bfbfbf' }}>
                        다른 날짜 범위를 선택해보세요.
                    </div>
                </div>
            )}

            {/* 날짜 범위 선택 */}
            <div style={{ marginBottom: 16 }}>
                <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder={['시작일', '종료일']}
                    style={{ marginRight: 8 }}
                />
                {dateRange && (
                    <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                        {dateRange[0].format('YYYY-MM-DD')} ~ {dateRange[1].format('YYYY-MM-DD')} 기간의 데이터
                    </span>
                )}
            </div>

            {/* 일자별 실행 통계 테이블 */}
            <Card title="📅 일자별 실행 통계" size="small" style={{ marginBottom: 16 }}>
                <Table
                    columns={dailyColumns}
                    dataSource={getDailyTableData()}
                    loading={loading}
                    scroll={{ x: 600 }}
                    pagination={{
                        pageSize: dailyTablePageSize,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `총 ${total}일`,
                        simple: window.innerWidth < 768,
                        onChange: (_page, size) => {
                            if (size && size !== dailyTablePageSize) {
                                setDailyTablePageSize(size);
                            }
                        },
                        onShowSizeChange: (_current, size) => {
                            setDailyTablePageSize(size);
                        }
                    }}
                    size="small"
                    locale={{
                        emptyText: (
                            <div style={{ padding: '40px 0', textAlign: 'center' }}>
                                <CalendarOutlined style={{ fontSize: '24px', color: '#d9d9d9', marginBottom: '8px' }} />
                                <div style={{ fontSize: '16px', color: '#8c8c8c', marginBottom: '8px' }}>
                                    선택한 기간에 실행 데이터가 없습니다
                                </div>
                                <div style={{ fontSize: '14px', color: '#bfbfbf' }}>
                                    다른 날짜 범위를 선택해보세요.
                                </div>
                            </div>
                        )
                    }}
                />
            </Card>

            {/* OrderTask별 총 실행 통계 테이블 (최근 24시간) */}
            <Card
                title={
                    <div>
                        📋 OrderTask별 총 실행 통계 (최근 24시간)
                        <span style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: '8px', fontWeight: 'normal' }}>
                            에러율 높은 순
                        </span>
                    </div>
                }
                size="small"
            >
                {/* 작업 유형 필터 버튼 */}
                <div style={{ marginBottom: '16px' }}>
                    <Button.Group>
                        <Button
                            type={taskTypeFilter === 'all' ? 'primary' : 'default'}
                            onClick={() => setTaskTypeFilter('all')}
                        >
                            전체
                        </Button>
                        <Button
                            type={taskTypeFilter === 'follow' ? 'primary' : 'default'}
                            onClick={() => setTaskTypeFilter('follow')}
                        >
                            Follow
                        </Button>
                        <Button
                            type={taskTypeFilter === 'like' ? 'primary' : 'default'}
                            onClick={() => setTaskTypeFilter('like')}
                        >
                            Like (일반/Reels)
                        </Button>
                    </Button.Group>
                </div>

                <Table
                    columns={orderTaskColumns}
                    dataSource={getOrderTaskTableData()}
                    loading={loading}
                    scroll={{ x: 800 }}
                    pagination={{
                        pageSize: orderTaskTablePageSize,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `총 ${total}개 OrderTask`,
                        simple: window.innerWidth < 768,
                        onChange: (_page, size) => {
                            if (size && size !== orderTaskTablePageSize) {
                                setOrderTaskTablePageSize(size);
                            }
                        },
                        onShowSizeChange: (_current, size) => {
                            setOrderTaskTablePageSize(size);
                        }
                    }}
                    size="small"
                    locale={{
                        emptyText: (
                            <div style={{ padding: '40px 0', textAlign: 'center' }}>
                                <InfoCircleOutlined style={{ fontSize: '24px', color: '#d9d9d9', marginBottom: '8px' }} />
                                <div style={{ fontSize: '16px', color: '#8c8c8c', marginBottom: '8px' }}>
                                    OrderTask 데이터가 없습니다
                                </div>
                                <div style={{ fontSize: '14px', color: '#bfbfbf' }}>
                                    스케줄이 실행되면 여기에 OrderTask별 통계가 표시됩니다.
                                </div>
                            </div>
                        )
                    }}
                />
            </Card>
        </Modal>
    );
};

export default ScheduleLogsModal;
